import { createSlice } from '@reduxjs/toolkit'

// Onboarding pertama kali untuk perusahaan baru. Milestone bisnis disimpan backend di option
// `finance_onboarding` (4 nilai, lihat FINANCE_ONBOARDING_BE); posisi step granular untuk
// resume di device yang sama tetap disimpan frontend di localStorage
// (lihat containers/FinanceOnboarding/storage.js).
export const FINANCE_ONBOARDING_STEP = {
  WELCOME: 'welcome',
  MENU_INVOICES: 'menu_invoices',
  BTN_ADD: 'btn_add',
  CONTACT_FIELD: 'contact_field',
  CONTACT_MODAL: 'contact_modal',
  PRODUCT_FIELD: 'product_field',
  PRODUCT_CATEGORY: 'product_category',
  PRODUCT_MODAL: 'product_modal',
  BTN_SAVE: 'btn_save',
  BTN_SEND: 'btn_send',
  WA_SEND: 'wa_send',
  WA_DONE: 'wa_done',
  PL_PANEL: 'pl_panel',
  DONE: 'done',
}

// Milestone yang divalidasi & disimpan backend pada option `finance_onboarding`.
// Frontend yang menulis tiap transisi (backend tidak auto-advance). Default/legacy =
// profit_loss_viewed (dianggap selesai). Lihat docs/be-finance-onboarding-persistence.md.
export const FINANCE_ONBOARDING_BE = {
  NEW: 'new',
  INVOICE_CREATED: 'invoice_created',
  WHATSAPP_SENT: 'whatsapp_sent',
  PROFIT_LOSS_VIEWED: 'profit_loss_viewed',
}

// Urutan funnel; dipakai untuk mencegah penulisan mundur ke backend.
export const FINANCE_ONBOARDING_BE_ORDER = {
  [FINANCE_ONBOARDING_BE.NEW]: 0,
  [FINANCE_ONBOARDING_BE.INVOICE_CREATED]: 1,
  [FINANCE_ONBOARDING_BE.WHATSAPP_SENT]: 2,
  [FINANCE_ONBOARDING_BE.PROFIT_LOSS_VIEWED]: 3,
}

const S = FINANCE_ONBOARDING_STEP
const BE = FINANCE_ONBOARDING_BE

// Kelompok besar (nilai inits) → step granular yang boleh di-resume dari localStorage.
// Device lain tidak punya step detail: masuk dari titik awal kelompok ini.
export const FINANCE_ONBOARDING_STEP_MILESTONE = {
  [S.WELCOME]: BE.NEW,
  [S.MENU_INVOICES]: BE.NEW,
  [S.BTN_ADD]: BE.NEW,
  [S.CONTACT_FIELD]: BE.NEW,
  [S.CONTACT_MODAL]: BE.NEW,
  [S.PRODUCT_FIELD]: BE.NEW,
  [S.PRODUCT_CATEGORY]: BE.NEW,
  [S.PRODUCT_MODAL]: BE.NEW,
  [S.BTN_SAVE]: BE.NEW,
  [S.BTN_SEND]: BE.INVOICE_CREATED,
  [S.WA_SEND]: BE.INVOICE_CREATED,
  [S.WA_DONE]: BE.WHATSAPP_SENT,
  [S.PL_PANEL]: BE.WHATSAPP_SENT,
  [S.DONE]: BE.PROFIT_LOSS_VIEWED,
}

export const isStepInFinanceOnboardingMilestone = (step, milestone) =>
  FINANCE_ONBOARDING_STEP_MILESTONE[step] === milestone

// Whitelist: onboarding aktif HANYA untuk 3 nilai awal. Nilai lain — profit_loss_viewed,
// kosong/null/undefined, key belum ada dari /inits, atau nilai tak dikenal — dianggap SELESAI.
// Default aman: user lama (BE set profit_loss_viewed, atau value belum terisi) tidak ke-onboarding.
export const isFinanceOnboardingActive = (value) =>
  value === FINANCE_ONBOARDING_BE.NEW ||
  value === FINANCE_ONBOARDING_BE.INVOICE_CREATED ||
  value === FINANCE_ONBOARDING_BE.WHATSAPP_SENT

// Transisi yang dipicu event dari komponen lain (modal kontak/produk, kirim WA).
// Transisi berbasis route ditangani di containers/FinanceOnboarding/index.jsx.
const EVENT_TRANSITIONS = {
  [S.CONTACT_FIELD]: {
    contact_modal_open: S.CONTACT_MODAL,
    contact_selected: S.PRODUCT_FIELD,
    contact_saved: S.PRODUCT_FIELD,
  },
  [S.CONTACT_MODAL]: {
    contact_saved: S.PRODUCT_FIELD,
    contact_modal_close: S.CONTACT_FIELD,
  },
  [S.PRODUCT_FIELD]: {
    // Modal Tambah Produk terbuka: kategori (wajib) dipandu dulu sebelum simpan.
    product_modal_open: S.PRODUCT_CATEGORY,
    product_selected: S.BTN_SAVE,
    product_saved: S.BTN_SAVE,
  },
  [S.PRODUCT_CATEGORY]: {
    product_category_selected: S.PRODUCT_MODAL,
    product_saved: S.BTN_SAVE,
    product_modal_close: S.PRODUCT_FIELD,
  },
  [S.PRODUCT_MODAL]: {
    product_saved: S.BTN_SAVE,
    product_modal_close: S.PRODUCT_FIELD,
  },
  // "Simpan & Kirim" membuka modal kirim bersamaan dengan pindah ke halaman detail,
  // jadi event buka modal bisa datang saat step masih BTN_SAVE.
  [S.BTN_SAVE]: {
    send_modal_open: S.WA_SEND,
  },
  [S.BTN_SEND]: {
    send_modal_open: S.WA_SEND,
  },
  [S.WA_SEND]: {
    whatsapp_sent: S.WA_DONE,
    send_modal_close: S.BTN_SEND,
  },
}

// snoozed: user menekan "Nanti saja". Step terakhir tetap disimpan (untuk widget progres &
// dilanjutkan), tapi spotlight, kunci navigasi, dan event transisi dimatikan.
const initialState = {
  step: null,
  snoozed: false,
}

const financeOnboardingSlice = createSlice({
  name: 'financeOnboarding',
  initialState,
  reducers: {
    setFinanceOnboardingStep: (state, { payload }) => {
      state.step = payload
    },
    // payload opsional: step yang dipulihkan dari localStorage saat reload.
    snoozeFinanceOnboarding: (state, { payload }) => {
      state.snoozed = true
      if (payload) {
        state.step = payload
      }
    },
    resumeFinanceOnboarding: (state) => {
      state.snoozed = false
    },
    resetFinanceOnboarding: () => initialState,
    // Dipanggil komponen lain; no-op bila step aktif tidak punya transisi untuk event tsb.
    financeOnboardingEvent: (state, { payload }) => {
      if (state.snoozed) {
        return
      }
      const next = EVENT_TRANSITIONS[state.step]?.[payload]
      if (next) {
        state.step = next
      }
    },
  },
})

export const {
  setFinanceOnboardingStep,
  snoozeFinanceOnboarding,
  resumeFinanceOnboarding,
  resetFinanceOnboarding,
  financeOnboardingEvent,
} = financeOnboardingSlice.actions

export default financeOnboardingSlice.reducer
